import styled from '@emotion/styled';
import { TableTag, Typography } from '@innovamat/glimmer-components';
import { dates } from '@innovamat/radiance-utils';
import { Trans, useTranslation } from 'react-i18next';
import { SCHEDULE_DEFAULT_VALUES } from '../../../../classrooms-management/constants';
import {
  hasHomeAccess as checkHasHomeAccess,
  type Classroom,
} from '../../../../classrooms-management/modules/classroom';
import { useRegionParameters } from '../../../../contents';
import { is12hClock } from '../../../../shared/modules/region/region-parameters';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`;

const Container = styled.div`
  display: flex;
  padding: 16px;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.surface['surface-secondary'].value};
  border-radius: 8px;
`;

const Content = styled.div`
  display: grid;
  height: 39px;
  row-gap: 6px;
  column-gap: 12px;
  flex: 1 0 0;
  grid-template-rows: repeat(1, minmax(0, 1fr));
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AccessStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledBody3 = styled(Typography.Body3)`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

type Props = {
  titleTranslationKey?: string;
  classroom?: Classroom;
  onClickModifyButton?: () => void;
};

function ClassroomConfigSection({
  titleTranslationKey = '',
  classroom,
  onClickModifyButton,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { regionParameters } = useRegionParameters({});
  const hasHomeAccess = checkHasHomeAccess(classroom);

  return (
    <Wrapper>
      {titleTranslationKey && (
        <Typography.Subtitle1>
          <Trans
            i18nKey={titleTranslationKey}
            defaults={titleTranslationKey}
            shouldUnescape={true}
            components={{
              b: <b />,
              br: <br />,
              li: <li />,
              ul: <ul />,
            }}
          />
        </Typography.Subtitle1>
      )}
      <Container>
        <Content>
          <Item>
            <StyledBody3>
              {t('reports.personalizedPractice.knowMore.text8')}
            </StyledBody3>
            <ItemContent>
              <Typography.Body2>
                {dates.formatTime({
                  dateTime:
                    classroom?.scheduleStart ||
                    SCHEDULE_DEFAULT_VALUES.scheduleStart,
                  hour12: is12hClock(regionParameters?.clock),
                })}
                {' - '}
                {dates.formatTime({
                  dateTime:
                    classroom?.scheduleEnd ||
                    SCHEDULE_DEFAULT_VALUES.scheduleEnd,
                  hour12: is12hClock(regionParameters?.clock),
                })}
              </Typography.Body2>
              <Typography.Link2 onClick={onClickModifyButton}>
                {t('reports.personalizedPractice.knowMore.buttonModify')}
              </Typography.Link2>
            </ItemContent>
          </Item>
          <Item>
            <StyledBody3>
              {t('reports.personalizedPractice.knowMore.text9')}
            </StyledBody3>
            <ItemContent>
              <Typography.Body2>
                {hasHomeAccess ? (
                  t('generic.status.active')
                ) : (
                  <AccessStatus>
                    {t('generic.status.disabled')}
                    <TableTag type="accent" text={t('recommended-tag')} />
                  </AccessStatus>
                )}
              </Typography.Body2>
            </ItemContent>
          </Item>
        </Content>
      </Container>
    </Wrapper>
  );
}

export { ClassroomConfigSection };
